import { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { AnimatePresence } from "framer-motion";
import { toast } from "react-toastify";
// import { Icon } from "@iconify-icon/react";
import Warning from "@/components/Icons/Warning";
import Loader from "@/components/Icons/Loader";
import Checkbox from "@/components/Icons/Checkbox";
// import FileInfo from "@/components/Icons/FileInfo";

// store
import useBrainStore from "@/store/_brainEmStore";
import useLinkStore from "@/store/_linkStore";
import { organismImgData } from "@/data/OrganismImgData";

// components
import TextBtn from "@/components/TextBtn/TextBtn";
import ImgSelectDialog from "./ImgSelectDialog";
import ExportHighlights from "./ExportHighlights";

// ---------------------------------------------------------------------------------

export default function SummaryBtnGroup({ isLinkMode }) {
	// Determine which store hook to use
	const useStore = isLinkMode ? useLinkStore : useBrainStore;
	const selectedOrganism = useStore((state) => state.organism); // Get selected organism

	// generateShareLink exists on both stores, use the dynamic hook
	const generateShareLink = useStore((state) => state.generateShareLink);
	// Select only the actions needed by this component
	const resetAll = useStore((state) => state.resetAll);

	// -------------------------------------------------------------------------------

	// State for Export btn
	// State to control the visibility of the popover
	const [isSelecting, setIsSelecting] = useState(false);
	// State to store the image style selected by the user in the dialog
	const [imgStyleToExport, setImgStyleToExport] = useState("sciIllustration");
	// For mounting and unmounting <ExportHighlights> in a portal
	const [isExporting, setIsExporting] = useState(false);
	// Refs for managing the export promise for toast.promise
	const resolvePngPromiseRef = useRef(null);
	const rejectPngPromiseRef = useRef(null);

	// for Create link btn
	const [generatingLink, setGeneratingLink] = useState(false);
	// for Reset btn
	const [spinIcon, setSpinIcon] = useState(false);

	// -------------------------------------------------------------------------------

	// Preload the photo image needed for the ImageSelectDialog
	useEffect(() => {
		// Ensure an organism is selected
		if (!selectedOrganism) return;

		// Get the image data for the selected organism
		const data = organismImgData[selectedOrganism];
		// Ensure data exists for the selected organism
		if (!data?.imagePhoto) return;

		// Create a new Image object to trigger the browser's download/cache mechanism
		const photoThumb = new Image();
		// Set the source to the photo image URL
		photoThumb.src = data.imagePhoto;

		// No need to attach event listeners like onload;
		// setting the 'src' is enough to initiate the request.
	}, [selectedOrganism]); // Re-run the effect if the selected organism changes

	// ------------------------------------------------------------------------------

	// User clicks 'Export' in popover
	function handleExportConfirm(selectedImgStyle) {
		setImgStyleToExport(selectedImgStyle); // Store the selected style
		setIsSelecting(false); // Close the popover
		// setIsExporting(true); // Trigger the export component // We'll move this

		// Define the function that will be called by toast.promise
		// This function needs to set up the export and return the promise
		function pngPromiseFn() {
			return new Promise((resolve, reject) => {
				resolvePngPromiseRef.current = resolve;
				rejectPngPromiseRef.current = reject;
				// Trigger the export component AFTER setting up promise resolution
				setIsExporting(true);
			});
		}

		// TOASTS
		toast.promise(pngPromiseFn(), {
			// pending: "Exporting go brr...",
			pending: {
				render: () => "Exporting go brr...",
				icon: <Loader className="toast-icon-loading" />,
			},
			// success: "Exported!",
			success: {
				render: () => "Exported!",
				icon: <Checkbox />,
				autoClose: 2500,
			},
			// error: "Export failed. Please try again.",
			error: {
				render: () => "Export failed. Please try again.",
				icon: <Warning />,
			},
		});
	}

	// ------------------------------------------------------------------------------

	// Callback for when ExportHighlights completes or errors
	function handleExportComplete(error) {
		if (error) {
			// toast.error("Highlights export failed. Please try again."); // Old toast, handled by promise now

			if (rejectPngPromiseRef.current) {
				// Pass the original error object, react-toastify can handle it.
				// The toast message itself is static from the .promise() config.
				rejectPngPromiseRef.current(error);
			}

			// Log the exact error for debugging
			console.error("Export completed with error:", error);

			// if no error, resolve the promise
		} else {
			if (resolvePngPromiseRef.current) {
				// No need to pass a value if the success message is static
				resolvePngPromiseRef.current();
			}
		}

		// Always ensure the exporting state is reset
		setIsExporting(false);

		// Clean up refs
		resolvePngPromiseRef.current = null;
		rejectPngPromiseRef.current = null;
	}

	// -----------------------------------------------------------------------------

	// For the 'Create link' btn
	async function handleGenerateLink() {
		// temporarily disable Create link btn
		setGeneratingLink(true);

		// get result from share.js
		const result = await generateShareLink();

		// TOASTS
		// IF result.success is true
		if (result.success) {
			if (result.copied) {
				toast.success(
					<div>
						<a
							href={result.shareUrl}
							target="_blank"
							rel="noopener noreferrer"
							className="toast-link toast-link-success"
						>
							Link
						</a>{" "}
						copied to clipboard.
					</div>,
					{
						icon: <Checkbox />,
					},
				);
				// IF result.copied is false
			} else {
				// use manual 'Click to copy' text button inside the toast
				toast.success(
					<div>
						<a
							href={result.shareUrl}
							target="_blank"
							rel="noopener noreferrer"
							className="toast-link toast-link-success"
						>
							Link
						</a>{" "}
						is ready.
					</div>,
					{
						autoClose: false,
						icon: <Checkbox />,
					},
				);
			}
			// IF result.success is false
		} else {
			toast.error(result.errorMessage, {
				icon: <Warning />,
			});
		}

		// re-enable Create link btn
		// Add a small delay, ~toast autoClose duration
		setTimeout(() => {
			setGeneratingLink(false);
		}, 5000);
	}

	// -----------------------------------------------------------------------------

	// For Reset btn
	function handleReset() {
		// Run resetAll from store
		resetAll();
		// Trigger spin on every click
		setSpinIcon(true);
		// Remove the spin class after animation completes
		setTimeout(() => setSpinIcon(false), 300);
	}

	// -----------------------------------------------------------------------------

	return (
		<>
			<AnimatePresence>
				{/* Conditionally render Image select dialog */}
				{isSelecting && (
					<ImgSelectDialog
						isLinkMode={isLinkMode}
						onCancel={() => setIsSelecting(false)} // Close dialog on cancel
						onConfirm={handleExportConfirm} // Handle when user confirms export in dialog
					/>
				)}
			</AnimatePresence>

			{/* --------------------------------------------------------------------- */}

			<div className="btn-group">
				{!isLinkMode ? (
					<p className="btn-heading">
						Your selections are saved in your browser until you clear site data.
						{/* You can refresh the page, close the tab, or quit the browser without
            losing data. */}
					</p>
				) : (
					<p className="btn-heading">
						Changes to shared links are saved only in your browser tab. Create a
						new link to share changes.
					</p>
				)}

				{/* --------------------------------------------------------------------- */}

				{/* EXPORT highlights btn */}
				<div className="text-btn-container">
					<TextBtn
						label="Export highlights"
						onClick={() => setIsSelecting(true)} // Open dialog
						icon={
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width={16}
								height={16}
								viewBox="0 0 24 24"
								aria-hidden="true"
							>
								<path
									fill="currentColor"
									stroke="currentColor"
									strokeWidth={0.0}
									d="M15 8V4H5v16h14V8zM3 2.992C3 2.444 3.447 2 3.999 2H16l5 5v13.993A1 1 0 0 1 20.007 22H3.993A1 1 0 0 1 3 21.008zM11 9.5a1.5 1.5 0 1 1-3 0a1.5 1.5 0 0 1 3 0m6.5 7.5l-4-7L8 17z"
								/>
							</svg>
						}
					/>
					<p className="btn-heading">as png</p>
				</div>

				{/* --------------------------------------------------------------------- */}

				{/* CREATE Link btn */}
				<p className="btn-heading">
					<TextBtn
						label="Create link"
						onClick={handleGenerateLink}
						disabled={generatingLink}
						icon={
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width={16}
								height={16}
								viewBox="0 0 24 24"
								aria-hidden="true"
							>
								<path
									fill="currentColor"
									stroke="currentColor"
									strokeWidth={0.0}
									d="m17.657 14.828l-1.415-1.414L17.658 12A4 4 0 1 0 12 6.343l-1.414 1.414L9.17 6.343l1.415-1.414a6 6 0 0 1 8.485 8.485zm-2.829 2.829l-1.414 1.414a6 6 0 0 1-8.485-8.485l1.414-1.414l1.414 1.414L6.343 12A4 4 0 0 0 12 17.657l1.414-1.414zm0-9.9l1.415 1.415l-7.072 7.07l-1.414-1.414z"
								/>
							</svg>
						}
					/>
					<span>to share or bookmark current selections</span>
				</p>

				{/* --------------------------------------------------------------------- */}

				{/* RESET btn */}
				{!isLinkMode ? (
					<div className="text-btn-container">
						<TextBtn
							className="danger-btn"
							label="Reset all"
							onClick={handleReset}
							icon={
								<svg
									className={spinIcon ? "spin" : ""}
									xmlns="http://www.w3.org/2000/svg"
									width={16}
									height={16}
									viewBox="0 0 24 24"
									fill="currentColor"
									stroke="currentColor"
									strokeWidth={0.0}
									aria-hidden="true"
								>
									<path d="M22 12c0 5.523-4.477 10-10 10S2 17.523 2 12S6.477 2 12 2v2a8 8 0 1 0 5.135 1.865L15 8V2h6l-2.447 2.447A9.98 9.98 0 0 1 22 12" />
								</svg>
							}
						/>
						<p className="btn-heading">to defaults</p>
					</div>
				) : (
					<div className="text-btn-container">
						<TextBtn
							className="danger-btn"
							label="Reset"
							onClick={handleReset}
							icon={
								<svg
									className={spinIcon ? "spin" : ""}
									xmlns="http://www.w3.org/2000/svg"
									width={16}
									height={16}
									viewBox="0 0 24 24"
									fill="currentColor"
									stroke="currentColor"
									strokeWidth={0.0}
									aria-hidden="true"
								>
									<path d="M22 12c0 5.523-4.477 10-10 10S2 17.523 2 12S6.477 2 12 2v2a8 8 0 1 0 5.135 1.865L15 8V2h6l-2.447 2.447A9.98 9.98 0 0 1 22 12" />
								</svg>
							}
						/>
						<p className="btn-heading">to link defaults</p>
					</div>
				)}
			</div>

			{/* Conditionally render ExportHighlights */}
			{isExporting && (
				<ExportHighlights
					onComplete={handleExportComplete}
					isLinkMode={isLinkMode}
					imgStyle={imgStyleToExport} // pass user-selected img style
				/>
			)}
		</>
	);
}

// -----------------------------------------------------------------------------

SummaryBtnGroup.propTypes = {
	isLinkMode: PropTypes.bool.isRequired,
};
